import { useEffect, useState } from "react";
import {
  articleApi,
  type ArticleUnitSummary,
  type DeletePlanWire,
  type DocumentsDisposition,
} from "../api/articleUnits";
import { ApiError } from "../api/types";
import { Button, TextField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { Trash2 } from "../compose/Icon";
import { formatBytes } from "../lib/fileIngest";
import { formatDate } from "../lib/format";

/**
 * Delete an article project — `DELETE /api/article-units/:id`.
 *
 * ── THE INVENTORY IS THE POINT ─────────────────────────────────────────────
 * On open this runs the SERVER's own dry run (`?dry_run=1`, which writes
 * nothing) and renders what it returns: every document by title and role, with
 * its real byte size, saved-version count, comment count, and a marker when it
 * has already been DELIVERED to the client. A confirmation dialog that says
 * "3 documents" without saying which and how big is how six delivered client
 * documents were destroyed — the server states the inventory precisely so the
 * client cannot get it wrong, and this renders that statement rather than
 * counting anything itself.
 *
 * ── THE COPY IS TRUE, WHICH CONSTRAINS THE WORDS ───────────────────────────
 * Neither disposition erases a document row; the server issues no
 * `DELETE FROM el.documents` on any path.
 *   · `detach` — the documents stay LIVE in the document library, with their
 *     sections, versions and comments intact. Only the project aggregate goes.
 *   · `archive` — the documents are additionally SOFT-archived: they leave every
 *     read surface (all of which filter `archived_at IS NULL`) and stay
 *     restorable. That is "archive". Writing "permanently delete" here would be
 *     a lie about what the button does, and users decide by the words.
 *
 * NEITHER choice is preselected. A destructive default is a default nobody
 * chose, so the confirm button stays inert until the user says which they mean;
 * and `archive` additionally requires typing the project's slug, which is
 * compared SERVER-side (a prompt that lives only in this modal protects nobody
 * who calls the API directly).
 */
export function DeleteArticleDialog({
  unit,
  onClose,
  onDeleted,
}: {
  unit: ArticleUnitSummary;
  onClose: () => void;
  /** The committed plan — the server's own count of what it just did. */
  onDeleted: (plan: DeletePlanWire) => void;
}) {
  const [plan, setPlan] = useState<DeletePlanWire | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // NO initial value — see the note above. `null` is "the user has not chosen".
  const [choice, setChoice] = useState<DocumentsDisposition | null>(null);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    let live = true;
    setLoading(true);
    articleApi
      .deleteArticleUnit(unit.id, { documents: "detach", dry_run: true })
      .then((p) => {
        if (!live) return;
        setPlan(p);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setLoading(false);
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : "Could not read what this project holds.",
        );
      });
    return () => {
      live = false;
    };
  }, [unit.id]);

  // The slug the server will compare against comes from the SERVER's own reply,
  // not from the row we were handed — the row could be stale after a rename.
  const slug = plan?.slug ?? unit.slug;
  const confirmed = confirmText.trim() === slug;
  const canSubmit =
    !busy &&
    !loading &&
    choice !== null &&
    (choice === "detach" || confirmed);

  async function commit() {
    if (choice === null) return;
    setError(null);
    setBusy(true);
    try {
      const done = await articleApi.deleteArticleUnit(unit.id, {
        documents: choice,
        // The confirmation rides ONLY on the archive path — a detach delete puts
        // no `confirm` on the wire at all.
        ...(choice === "archive" ? { confirm: confirmText.trim() } : {}),
      });
      setBusy(false);
      onDeleted(done);
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not delete the project.",
      );
    }
  }

  const docs = plan?.documents ?? [];

  return (
    <Dialog
      title={`Delete “${unit.title}”?`}
      subtitle="The project goes. What happens to its documents is your choice — and nothing is erased either way."
      onClose={onClose}
      testid="delete-article-dialog"
    >
      {error ? (
        <div className="auth__error" role="alert" data-testid="da-error">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="field__hint" role="status" data-testid="da-loading">
          Reading what this project holds…
        </p>
      ) : (
        <div className="field">
          <span className="field__label">
            {docs.length === 0
              ? "This project has no documents."
              : `This project holds ${docs.length} document${docs.length === 1 ? "" : "s"}`}
          </span>
          <ul
            className="da-inventory"
            data-testid="da-inventory"
            style={{ listStyle: "none", margin: 0, padding: 0 }}
          >
            {docs.map((d) => (
              <li
                key={d.document_id}
                data-testid={`da-doc-${d.document_id}`}
                style={{
                  borderTop: "1px solid var(--norkron-hairline, #2a2a2a)",
                  padding: "8px 0",
                }}
              >
                <div className="docrow__title">{d.title}</div>
                <div className="docrow__meta">
                  <span data-testid={`da-doc-role-${d.document_id}`}>
                    {d.resource_role}
                  </span>
                  <span className="dot">·</span>
                  <span data-testid={`da-doc-size-${d.document_id}`}>
                    {formatBytes(d.byte_size)}
                  </span>
                  <span className="dot">·</span>
                  <span data-testid={`da-doc-versions-${d.document_id}`}>
                    {d.version_count} version{d.version_count === 1 ? "" : "s"}
                  </span>
                  <span className="dot">·</span>
                  <span data-testid={`da-doc-comments-${d.document_id}`}>
                    {d.comment_count} comment{d.comment_count === 1 ? "" : "s"}
                  </span>
                  {d.delivered_at ? (
                    <>
                      <span className="dot">·</span>
                      <strong data-testid={`da-doc-delivered-${d.document_id}`}>
                        Delivered to the client {formatDate(d.delivered_at)}
                      </strong>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <fieldset
        className="field"
        style={{ border: 0, margin: 0, padding: 0 }}
        data-testid="da-choices"
      >
        <legend className="field__label">What happens to these documents?</legend>

        <label
          htmlFor="da-choice-detach"
          style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}
        >
          <input
            id="da-choice-detach"
            type="radio"
            name="da-disposition"
            data-testid="da-choice-detach"
            checked={choice === "detach"}
            onChange={() => setChoice("detach")}
          />
          <span>
            <strong>Keep the documents in the library</strong>
            <span className="field__hint" style={{ display: "block", margin: 0 }}>
              They stay live and readable in the document library with every
              version and comment intact — only the project that grouped them goes.
            </span>
          </span>
        </label>

        <label
          htmlFor="da-choice-archive"
          style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0" }}
        >
          <input
            id="da-choice-archive"
            type="radio"
            name="da-disposition"
            data-testid="da-choice-archive"
            checked={choice === "archive"}
            onChange={() => setChoice("archive")}
          />
          <span>
            <strong>Archive the documents too</strong>
            <span className="field__hint" style={{ display: "block", margin: 0 }}>
              They leave the library and every other read surface, and can be
              restored later. Nothing is erased — no version, comment or delivery
              record is destroyed.
            </span>
          </span>
        </label>
      </fieldset>

      {choice === "archive" ? (
        <div data-testid="da-confirm-block">
          <TextField
            label={`Type “${slug}” to confirm`}
            hint="The server compares this against the project's own slug — it is not a formality this dialog checks by itself."
            value={confirmText}
            data-testid="da-confirm"
            autoFocus
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </div>
      ) : null}

      <div className="dialog__actions">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          data-testid="da-submit"
          disabled={!canSubmit}
          title={
            choice === null
              ? "Choose what happens to the documents first."
              : choice === "archive" && !confirmed
                ? `Type “${slug}” to confirm.`
                : undefined
          }
          onClick={commit}
          icon={<Trash2 size={15} strokeWidth={1.5} />}
        >
          {busy
            ? "Deleting…"
            : choice === "archive"
              ? "Delete the project and archive its documents"
              : "Delete the project"}
        </Button>
      </div>
    </Dialog>
  );
}
